/**
 * 폭 맞춤 표의 공용 기계 — 측정, 패킹, 가용폭 관측, 셀 스타일.
 *
 * 마크다운 표(FitTable)와 계층 표(TreeTable)가 같은 말풍선 안에 나란히 서므로
 * 열을 접는 규칙도 셀의 생김새도 같아야 한다. 두 벌로 두면 조용히 갈라진다.
 *
 * 측정은 DOM 이 아니라 canvas measureText 로 한다.
 *   - 셀 자연폭은 (데이터·폰트·언어)에만 의존하고, 열 몇 개를 담을지는 (자연폭·가용폭)에만
 *     의존한다. 핸들 드래그는 후자만 바꾸므로 O(셀) 측정과 O(열) 패킹을 분리할 수 있다.
 *   - DOM 측정은 열에 폭을 입히는 순간 자연폭을 잃어 되먹임을 피할 수 없고(표를 두 번
 *     그리지 않는 한), getBoundingClientRect 는 드래그 중 동기 리플로우다.
 *   - canvas 오차는 table-layout:fixed 아래에서 한 줄 일찍 줄바꿈될 뿐 절대 넘치지 않는다.
 *
 * 폭(px)은 표현 값이라 React state 로 두지 않고 <col>.style.width 에 직접 쓴다
 * (scenarioStatusUi.StatusItemList 와 같은 이유) — React 리렌더는 보이는 열 개수가
 * 바뀌는 순간에만 일어난다.
 *
 * 중립 leaf — pages/** 를 import 하지 않는다(aiMarkdown.js 와 같은 규약).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { getFontFamily, getFontSizePx } from "@plannel/utils/font-util";

export const CELL_PAD = 14;        // px "6px" 양쪽 12 + 테두리·반올림 여유 2
export const MIN_COL = 46;         // 12px 한글 3자 — 이보다 좁으면 열이 아니라 잡음이다
export const MAX_TEXT = 200;       // 텍스트 열 상한 — 넘치는 만큼은 줄바꿈으로 소화
export const MAX_NUM = 130;        // 숫자 열 상한 — 숫자는 줄바꿈하면 못 읽으므로 상한도 낮다
export const MEASURE_ROWS = 200;   // 자연폭 표본 상한 — 그 이상 행은 줄바꿈으로 소화
// 계층 열 상한 — 들여쓰기가 라벨 앞에 붙어 같은 글자 수라도 텍스트 열보다 넓게 필요하다.
export const MAX_TREE = 280;

// 측정(canvas measureText·폴백)은 flo 흐름도 렌더러와 함께 쓰는 공용 유틸로 옮겼다.
export { measureText } from "@plannel/utils/text-measure-util";

/**
 * 열별 폭 상한. 숫자·텍스트·계층 열이 서로 다른 값을 쓴다.
 *
 * @param {boolean[]} isNum 열별 숫자 여부
 * @param {number} treeCol  계층 열의 인덱스. 없으면 -1
 */
export function columnCaps(isNum, treeCol = -1) {
  return isNum.map((num, i) => (i === treeCol ? MAX_TREE : (num ? MAX_NUM : MAX_TEXT)));
}

/**
 * 자연폭 배열을 가용폭에 담는다 — 왼쪽부터 채우고 뒤를 생략(스크롤 0 위치에서 자른 그림과
 * 같아 예측 가능). 첫 열은 무조건 남긴다.
 *
 * @param {number[]} nat   열별 자연폭(px)
 * @param {number[]} caps  열별 폭 상한(px) — columnCaps 가 만든다
 * @param {number} avail   가용폭(px)
 * @returns {{widths: number[], k: number}} 보이는 열 폭 배열과 개수. 합은 정확히 avail
 *   (첫 열조차 안 들어가는 경우만 예외 — avail 전부를 첫 열에 주고 줄바꿈에 맡긴다)
 */
export function fitColumns(nat, caps, avail) {
  const cap = nat.map((w, i) => Math.max(MIN_COL, Math.min(Math.ceil(w), caps[i])));
  let k = 1;
  let sum = cap[0];
  while (k < cap.length && sum + cap[k] <= avail) { sum += cap[k]; k += 1; }
  const w = cap.slice(0, k);
  if (sum > avail) {
    // 첫 열조차 안 들어간다 — 가용폭 전부를 주고 줄바꿈에 맡긴다(fixed 라 넘치지 않는다)
    w[0] = Math.max(MIN_COL, avail);
    return { widths: w, k };
  }
  // 남는 폭 ①: MAX 상한으로 잘렸던 열을 자연폭까지 되돌린다(긴 텍스트 열이 먼저 한 줄이 된다)
  let left = avail - sum;
  for (let i = 0; i < k && left > 0; i += 1) {
    const want = Math.ceil(nat[i]) - w[i];
    if (want > 0) { const g = Math.min(want, left); w[i] += g; left -= g; }
  }
  // 남는 폭 ②: 폭 비례 분배 — 표가 avail 을 정확히 채워 오른쪽 끝이 들쭉날쭉하지 않게.
  //   반올림 잔여는 마지막 보이는 열에 몰아 합을 정확히 맞춘다.
  if (left > 0) {
    const total = w.reduce((a, b) => a + b, 0);
    let used = 0;
    for (let i = 0; i < k - 1; i += 1) {
      const g = Math.floor((left * w[i]) / total);
      w[i] += g; used += g;
    }
    w[k - 1] += left - used;
  }
  return { widths: w, k };
}

/**
 * 측정에 쓸 폰트 문자열과 해석된 크기.
 *
 * 폰트 문자열은 CSS 변수(var(--app-font-family))가 아니라 실문자열이어야 한다 —
 * canvas 는 var() 를 해석하지 못한다(font-util.js 의 canvas 렌더러 규약과 동일).
 *   크기도 마찬가지다 — FONT.BODY 는 var() 문자열이므로 해석된 px 를 넣어야 한다.
 * 웹폰트 로드 전 첫 측정은 폴백 폰트 기준이라 폭이 어긋난다 — 로드 완료 시 1회 재측정한다.
 */
export function useCellFont() {
  const { i18n } = useTranslation();
  const [fontEpoch, setFontEpoch] = useState(0);
  useEffect(() => {
    let on = true;
    document.fonts?.ready?.then(() => { if (on) setFontEpoch((e) => e + 1); });
    return () => { on = false; };
  }, []);
  const bodyPx = getFontSizePx("body");
  const family = getFontFamily(i18n.language);
  return {
    lang: i18n.language, fontEpoch, bodyPx,
    bodyFont: `${bodyPx}px ${family}`,
    boldFont: `700 ${bodyPx}px ${family}`,
  };
}

/**
 * 가용폭을 관측해 <col> 폭을 직접 쓰고, 보이는 열 개수만 state 로 낸다.
 *
 * natRef 는 { w: number[], caps: number[] } 를 들고 있어야 한다 — 호출부가 렌더마다 채운다.
 * ref 로 읽는 이유는 의존성 배열을 비워 applyFit 의 정체성을 고정하기 위함이다.
 *
 * @param {object} natRef 자연폭과 상한을 담은 ref
 * @param {number} colCount 열 개수 — visK 초기값
 */
export function useColumnFit(natRef, colCount) {
  const scrollRef = useRef(null);     // 가용폭 측정 대상 — 폭이 콘텐츠에 좌우되지 않는 지점
  const colRefs = useRef([]);
  const widthsRef = useRef([]);
  const availRef = useRef(0);
  const rafRef = useRef(0);
  const [visK, setVisK] = useState(colCount || 1);

  const applyFit = useCallback(() => {
    const avail = availRef.current;
    if (!avail || !natRef.current.w.length) return;
    const caps = natRef.current.caps || [];
    const { widths, k } = fitColumns(natRef.current.w, caps, avail);
    widthsRef.current = widths;
    // 열 개수가 그대로면 React 를 깨우지 않고 <col> 폭만 직접 쓴다 —
    // 핸들 드래그 1px 마다 300행 표를 다시 그리지 않기 위함
    for (let i = 0; i < widths.length; i += 1) {
      const el = colRefs.current[i];
      if (el) el.style.width = `${widths[i]}px`;
    }
    setVisK((prev) => (prev === k ? prev : k));
  }, [natRef]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    // 첫 페인트 전에 동기 실측으로 시드 — ResizeObserver 첫 발화(페인트 후)를 기다리면
    // 전체 열이 균등 압축된 표가 한 프레임 보였다가 줄어드는 깜빡임이 생긴다
    availRef.current = el.clientWidth || 0;
    applyFit();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[entries.length - 1].contentRect.width);
      if (!w || w === availRef.current) return;
      availRef.current = w;
      // 관찰 콜백 안에서 바로 폭을 고치면 안 된다 — 폭을 줄이면 셀이 줄바꿈되며 표가 높아지고,
      //   그 높이 변화가 대화 목록의 세로 스크롤바를 오가게 해 폭이 다시 바뀐다. 같은 전달
      //   주기 안에서 그 되먹임이 일어나면 브라우저가 "ResizeObserver loop" 로 끊는다.
      //   다음 프레임으로 미루면 되먹임이 주기를 건너 일어나므로 끊기지 않고 수렴한다.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; applyFit(); });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    };
  }, [applyFit]);

  // 데이터·측정이 갱신될 때마다 재패킹 — 열 개수가 안 바뀌면 setState 하지 않아 종결된다
  useLayoutEffect(() => { applyFit(); });

  return { scrollRef, colRefs, widthsRef, visK };
}

export const CELL_SX = {
  border: `0.5px solid ${C.border}`, px: "6px", py: "3px",
  fontSize: FONT.BODY, verticalAlign: "top",
  // nowrap 금지 — 좁으면 여러 줄, 넓히면 한 줄.
  // 끊을 곳 없는 긴 토큰(SKU·URL)도 강제로 끊고, 그래도 넘치는 것은 hidden 이 마지막 방어선이다.
  whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere",
  overflow: "hidden",
  boxSizing: "border-box",   // <col> 폭 = border-box 폭이라는 패킹 계산의 전제
};

export const headSx = (maxHeight) => ({
  ...CELL_SX, bgcolor: C.bg3, fontWeight: 700,
  ...(maxHeight ? { position: "sticky", top: 0, zIndex: 2 } : null),
});

/**
 * 표 아래 한 줄 안내.
 *
 * 안내에 마우스를 올리면 무언가 나온다는 것을 글만으로는 알 수 없다.
 * 물음표 아이콘을 붙여 물어볼 곳이 있음을 드러내고 툴팁은 아이콘에만 건다.
 * 무엇을 안내하는지는 부르는 쪽이 정한다 — 열이 생략된 것과 행이 잘린 것은 원인도 해법도 다르다.
 */
export function NoteLine({ text, tip }) {
  if (!text) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "3px", mt: "2px" }}>
      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{text}</Typography>
      <Tooltip title={tip} placement="top" arrow>
        <HelpOutlineIcon sx={{ fontSize: 12, color: C.text3, cursor: "help" }} />
      </Tooltip>
    </Box>
  );
}

/** 생략된 열 개수 안내 — 패널을 넓히면 사라지는 종류다. */
export function OmittedColsNote({ n }) {
  const { t } = useTranslation();
  if (n <= 0) return null;
  return <NoteLine text={t("agentChatTableOmitted", { n })} tip={t("agentChatTableOmittedTip")} />;
}
